import { CUSTOM_FILTER_DELETED } from '../../../filterQueryFormat/UsageSubActionTypes';
import { MAX_CUSTOM_QUICK_FILTERS } from './MoreQuickFiltersPopoverContent';
import { USED_FILTERS_BAR } from '../../../filterQueryFormat/UsageActionTypes';
import { List, OrderedMap } from 'immutable';
import { isOperator } from '../../../filterQueryFormat/operator/Operator';
import { removeKeyPath } from '../../../filterQueryFormat/logic/LogicGroup';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQuickFiltersOnChangeHandler } from '../../../hooks/useQuickFiltersOnChangeHandler';
import DraggableQuickFilter from './DraggableQuickFilter';
import EmptySlot from './EmptySlot';
import FilterUserAction from '../../../filterQueryFormat/FilterUserAction';
import FormattedMessage from 'I18n/components/FormattedMessage';
import SegmentationFiltersInstanceContext from '../../../state/SegmentationFiltersInstanceContext';
import { Button } from 'foundations-components/transitional/components/Button';
import { LeftIcon } from 'foundations-assets';
import styled from 'foundations-theming/utils';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const QuickFiltersComponentsContainer = styled.div.withConfig({
  displayName: "EditQuickFiltersPopoverContent__QuickFiltersComponentsContainer",
  componentId: "sc-1qo6nyj-0"
})(["display:flex;gap:4px;"]);
const getSelectedOptionsCount = condition => {
  if (!condition || !isOperator(condition)) {
    return 0;
  }
  if (Array.isArray(condition.values)) {
    return condition.values.length;
  }
  if (List.isList(condition.value)) {
    return condition.value.size;
  }
  return 1;
};
const EditQuickFiltersPopoverContent = ({
  buttonProperties,
  customPropertyNames,
  enableReordering,
  hasOverflowingQuickFilters,
  onClosePopover,
  onCustomPropertyNamesChange,
  setCurrentPopoverContent
}) => {
  const {
    getTrackerInstance,
    props: {
      baseFilterFamily,
      onUserAction
    },
    quickFiltersInternalValue
  } = useContext(SegmentationFiltersInstanceContext);
  const [persistedPropertyNames, setPersistedPropertyNames] = useState(customPropertyNames);
  const onQuickFiltersChange = useQuickFiltersOnChangeHandler();
  const quickFiltersConditions = quickFiltersInternalValue ? quickFiltersInternalValue.conditions : OrderedMap();
  const isUnmounted = useRef(false);
  useEffect(() => {
    return () => {
      isUnmounted.current = true;
    };
  }, []);
  const onRemoveDeletedConditions = useCallback(deletedPropertyNames => {
    if (!quickFiltersInternalValue || deletedPropertyNames.length === 0) {
      return;
    }
    const newQuickFiltersInternalValue = quickFiltersConditions.entrySeq().reduce((acc, [key, value]) => {
      if (key && isOperator(value) && value.field && deletedPropertyNames.includes(value.field.name)) {
        const keyPath = List.of(key);
        return removeKeyPath(keyPath, acc);
      }
      return acc;
    }, quickFiltersInternalValue);
    if (quickFiltersInternalValue.equals(newQuickFiltersInternalValue)) {
      return;
    }
    onQuickFiltersChange(newQuickFiltersInternalValue);
  }, [onQuickFiltersChange, quickFiltersConditions, quickFiltersInternalValue]);
  useEffect(() => {
    return () => {
      if (isUnmounted.current) {
        const deletedPropertyNames = customPropertyNames.filter(propertyName => !persistedPropertyNames.includes(propertyName));
        onRemoveDeletedConditions(deletedPropertyNames);
        onCustomPropertyNamesChange(persistedPropertyNames);
      }
    };
  }, [customPropertyNames, onCustomPropertyNamesChange, onRemoveDeletedConditions, persistedPropertyNames]);
  const onDeleteQuickFilter = index => {
    var _getTrackerInstance;
    const newPersistedPropertyNames = [...persistedPropertyNames];
    const [deletedPropertyName] = newPersistedPropertyNames.splice(index, 1);
    setPersistedPropertyNames(newPersistedPropertyNames);
    (_getTrackerInstance = getTrackerInstance()) === null || _getTrackerInstance === void 0 || _getTrackerInstance.track('removedProperty', {
      primaryObjectTypeId: baseFilterFamily,
      version: '2',
      property: deletedPropertyName
    });
    onUserAction(FilterUserAction({
      action: USED_FILTERS_BAR,
      subAction: CUSTOM_FILTER_DELETED,
      filterFamily: baseFilterFamily,
      property: deletedPropertyName
    }));
  };
  const onFinishReordering = property => {
    var _getTrackerInstance2;
    const quickFiltersContainer = document.querySelector('#edit-popover-quick-filters-container');
    if (!quickFiltersContainer) {
      return;
    }
    const newPersistedPropertyNames = [...quickFiltersContainer.querySelectorAll('.draggable-quick-filter')].map(el => el.id);
    setPersistedPropertyNames(newPersistedPropertyNames);
    (_getTrackerInstance2 = getTrackerInstance()) === null || _getTrackerInstance2 === void 0 || _getTrackerInstance2.track('reorderedProperty', {
      primaryObjectTypeId: baseFilterFamily,
      version: '2',
      property
    });
  };
  const onDragOver = evt => {
    evt.preventDefault();
    evt.stopPropagation();
    evt.dataTransfer.dropEffect = 'move';
    const quickFiltersContainer = document.querySelector('#edit-popover-quick-filters-container');
    const draggingItem = quickFiltersContainer === null || quickFiltersContainer === void 0 ? void 0 : quickFiltersContainer.querySelector('.dragging');
    if (!quickFiltersContainer || !draggingItem) {
      return;
    }

    // Get all items except the dragging item and make an array of them
    const siblings = [...quickFiltersContainer.querySelectorAll('.draggable-quick-filter:not(.dragging)')].filter(el => el instanceof HTMLElement);
    // Find the sibling that should be after the dragging item
    const nextSibling = siblings.find(sibling => {
      const mouseXPosition = evt.clientX;
      const siblingMiddleXPosition = sibling.getBoundingClientRect().left + sibling.offsetWidth / 2;
      return mouseXPosition <= siblingMiddleXPosition;
    });
    if (nextSibling) {
      // Insert the dragging item before the found sibling
      quickFiltersContainer.insertBefore(draggingItem, nextSibling);
      return;
    }
    const lastSibling = siblings.pop();
    const firstEmptySlot = lastSibling === null || lastSibling === void 0 ? void 0 : lastSibling.nextSibling;
    if (firstEmptySlot) {
      // Insert the dragging item before the first empty slot
      quickFiltersContainer.insertBefore(draggingItem, firstEmptySlot);
    } else {
      // Insert the dragging item at the end
      quickFiltersContainer.appendChild(draggingItem);
    }
  };
  return /*#__PURE__*/_jsxs(View, {
    as: "div",
    padding: "400",
    children: [!hasOverflowingQuickFilters && persistedPropertyNames.length === MAX_CUSTOM_QUICK_FILTERS && /*#__PURE__*/_jsx(Flex, {
      justifyContent: "center",
      children: /*#__PURE__*/_jsx(Text, {
        color: "text-core-disabled",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.quickFiltersBar.addQuickFilterDisabled"
        })
      })
    }), (hasOverflowingQuickFilters || customPropertyNames.length < MAX_CUSTOM_QUICK_FILTERS) && /*#__PURE__*/_jsx(Button, {
      size: "sm",
      use: "transparent",
      onClick: () => setCurrentPopoverContent(''),
      padding: "0",
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "100",
        children: [/*#__PURE__*/_jsx(LeftIcon, {
          size: "100",
          color: "icon-core-subtle"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.quickFiltersBar.back"
        })]
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      justifyContent: "space-between",
      alignItems: "center",
      marginBlock: "200",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h5",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.quickFiltersBar.editQuickFilters"
        })
      }), /*#__PURE__*/_jsx(Button, {
        "data-selenium-test": "EditPopover-apply",
        disabled: customPropertyNames.toString() === persistedPropertyNames.toString(),
        size: "xs",
        use: "tertiary",
        onClick: onClosePopover,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.quickFiltersBar.apply"
        })
      })]
    }), /*#__PURE__*/_jsxs(QuickFiltersComponentsContainer, {
      id: "edit-popover-quick-filters-container",
      onDragEnter: evt => evt.preventDefault(),
      onDragOver: onDragOver,
      children: [persistedPropertyNames.map((propertyName, index) => {
        const condition = quickFiltersConditions.find(quickFiltersCondition => isOperator(quickFiltersCondition) && !!quickFiltersCondition.field && quickFiltersCondition.field.name === propertyName);
        const selectedOptionsCount = getSelectedOptionsCount(condition);
        return /*#__PURE__*/_jsx(DraggableQuickFilter, {
          buttonProperties: buttonProperties,
          draggable: enableReordering && persistedPropertyNames.length > 1,
          onDeleteQuickFilter: () => onDeleteQuickFilter(index),
          onFinishReordering: onFinishReordering,
          propertyName: propertyName,
          selectedOptionsCount: selectedOptionsCount
        }, `${propertyName}-edit-popover-filter-component`);
      }), [...Array(MAX_CUSTOM_QUICK_FILTERS - persistedPropertyNames.length)].map((_, index) => {
        return /*#__PURE__*/_jsx(EmptySlot, {
          buttonHeight: buttonProperties.BUTTON_HEIGHT
        }, `empty-slot-${index}`);
      })]
    })]
  });
};
export default EditQuickFiltersPopoverContent;